import { useCallback, useState, MouseEvent } from 'react';
import classNames from 'classnames';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconDownload } from '@picsart/design-system/foundation/icons/IconDownload';
import { IconShareOutline } from '@picsart/design-system/foundation/icons/IconShareOutline';
import { IconNewWindow } from '@picsart/design-system/foundation/icons/IconNewWindow';
import ReactionButtons from '@/components/ReactionButtons';
import useStyles from './styles';
import type { CardActionsProps } from './types';

const CardActions = ({
  onDownload,
  onShare,
  onReact,
  reaction,
  onOpenInEditor,
  isParentHovered,
}: CardActionsProps) => {
  const classes = useStyles();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (isDownloading) return;
      setIsDownloading(true);
      try {
        await onDownload?.();
      } finally {
        setIsDownloading(false);
      }
    },
    [onDownload, isDownloading]
  );
  const handleShare = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onShare?.();
    },
    [onShare]
  );
  const handleOpenInEditor = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onOpenInEditor?.();
    },
    [onOpenInEditor]
  );

  if (!onDownload && !onShare && !onReact && !onOpenInEditor) return null;

  return (
    <div
      className={classNames(classes.actionsContainer, {
        [classes.actionsContainerVisible]: isParentHovered,
      })}
    >
      <div className={classes.group}>
        {onReact && (
          <ReactionButtons
            reaction={reaction}
            onReact={onReact}
            size="small"
            className={classes.iconButton}
            dataTestIdPrefix="card-action"
            isWithoutBorder
          />
        )}
      </div>

      <div className={classes.group}>
        {onOpenInEditor && (
          <InlineButton
            size="small"
            centerIcon={IconNewWindow}
            onClick={handleOpenInEditor}
            aria-label="Open in Editor"
            className={classes.iconButton}
            dataTestId="card-action-open-in-editor"
            isWithoutBorder
          />
        )}
        {onDownload && (
          <InlineButton
            size="small"
            centerIcon={IconDownload}
            onClick={handleDownload}
            isDisabled={isDownloading}
            isLoading={isDownloading}
            aria-label={isDownloading ? 'Downloading' : 'Download'}
            className={classes.iconButton}
            dataTestId="card-action-download"
            isWithoutBorder
          />
        )}
        {onShare && (
          <InlineButton
            size="small"
            centerIcon={IconShareOutline}
            onClick={handleShare}
            aria-label="Share"
            className={classes.iconButton}
            dataTestId="card-action-share"
            isWithoutBorder
          />
        )}
      </div>
    </div>
  );
};

export default CardActions;
